import { useState } from 'react';
import {
  Bell,
  Plus,
  Trash2,
  Edit,
  Users,
  AlertTriangle,
  AlertCircle,
  Info,
  MoreHorizontal,
  ChevronRight,
  UserPlus,
  X,
  Check,
  Loader2,
  Shield,
  Clock,
  History,
  Search,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useAlertGroups,
  useAlertGroupMembers,
  useAlertRules,
  useCreateAlertGroup,
  useUpdateAlertGroup,
  useDeleteAlertGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useCreateAlertRule,
  useUpdateAlertRule,
  useDeleteAlertRule,
  getRuleTypeLabel,
  getSeverityColor,
  type AlertRuleType,
  type AlertSeverity,
  type AlertGroup,
  type AlertRule,
} from '@/hooks/useAlerts';
import { useTeam } from '@/hooks/useTeam';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { usePendingApprovals, type PendingApproval } from '@/hooks/usePendingApprovals';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatDistanceToNow, format } from 'date-fns';

type ActiveTab = 'rules' | 'groups' | 'approvals';

const RULE_TYPES: { value: AlertRuleType; label: string; description: string }[] = [
  { value: 'order_waiting', label: 'Order Waiting Too Long', description: 'Alert when orders wait beyond a threshold' },
  { value: 'kitchen_order_waiting', label: 'Kitchen Order Exceeds SLA', description: 'Alert when kitchen prep time exceeds threshold' },
  { value: 'order_ready', label: 'Order Ready - Not Picked Up', description: 'Alert when ready orders are not collected' },
  { value: 'low_stock', label: 'Low Stock Alert', description: 'Alert when inventory falls below threshold' },
  { value: 'register_open', label: 'Register Inactive', description: 'Alert when register has no activity' },
  { value: 'shift_overtime', label: 'Shift Overtime', description: 'Alert when employee shift runs over' },
];

const SEVERITY_OPTIONS: { value: AlertSeverity; label: string; icon: typeof Info }[] = [
  { value: 'info', label: 'Info', icon: Info },
  { value: 'warning', label: 'Warning', icon: AlertTriangle },
  { value: 'critical', label: 'Critical', icon: AlertCircle },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

interface ApprovalHistoryItem {
  id: string;
  action_type: string;
  action_metadata: Record<string, unknown>;
  status: string;
  created_at: string;
  resolved_at: string | null;
  requester_name: string;
  approver_name: string | null;
}

export default function Alerts() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('rules');
  
  // Check if user can view approvals
  const canViewApprovals = profile?.role === 'owner' || profile?.role === 'primary_admin';
  
  // Alert Rules state
  const { data: rules = [], isLoading: rulesLoading } = useAlertRules();
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  
  // Alert Groups state
  const { data: groups = [], isLoading: groupsLoading } = useAlertGroups();
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AlertGroup | null>(null);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  
  // Form state for rules
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    rule_type: 'order_waiting' as AlertRuleType,
    severity: 'warning' as AlertSeverity,
    location_id: null as string | null,
    alert_group_id: null as string | null,
    send_email: true,
    send_in_app: true,
    cooldown_minutes: 15,
    threshold_minutes: 15,
    active_days: [0, 1, 2, 3, 4, 5, 6],
    is_active: true,
  });
  
  // Form state for groups
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
  });

  // Hooks
  const { teamMembers } = useTeam();
  const { locations } = useLocations();
  const { data: groupMembers = [] } = useAlertGroupMembers(selectedGroupId);
  
  // Approvals hooks
  const {
    pendingApprovals,
    isLoading: approvalsLoading,
    approve,
    deny,
    isApproving,
    isDenying,
    formatActionType,
    getActionDescription,
  } = usePendingApprovals();
  
  // Approval state
  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [approvalSearchTerm, setApprovalSearchTerm] = useState('');
  
  // Fetch approval history
  const { data: approvalHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['approval-history', profile?.organization_id],
    queryFn: async (): Promise<ApprovalHistoryItem[]> => {
      if (!profile?.organization_id) return [];

      const { data, error } = await supabase
        .from('owner_2fa_approvals')
        .select(`
          id,
          action_type,
          action_metadata,
          approved_at,
          requester:requester_user_id (first_name, last_name),
          approver:approver_user_id (first_name, last_name)
        `)
        .eq('organization_id', profile.organization_id)
        .order('approved_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching approval history:', error);
        return [];
      }

      return (data || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        action_type: item.action_type as string,
        action_metadata: item.action_metadata as Record<string, unknown>,
        status: 'approved',
        created_at: item.approved_at as string,
        resolved_at: item.approved_at as string,
        requester_name: formatApprovalName(item.requester as { first_name?: string; last_name?: string }),
        approver_name: formatApprovalName(item.approver as { first_name?: string; last_name?: string }),
      }));
    },
    enabled: !!profile?.organization_id && activeTab === 'approvals' && canViewApprovals,
  });

  function formatApprovalName(user?: { first_name?: string; last_name?: string }): string {
    if (!user) return 'Unknown';
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  }
  
  const createRule = useCreateAlertRule();
  const updateRule = useUpdateAlertRule();
  const deleteRule = useDeleteAlertRule();
  
  const createGroup = useCreateAlertGroup();
  const updateGroup = useUpdateAlertGroup();
  const deleteGroup = useDeleteAlertGroup();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();

  // Reset rule form
  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      rule_type: 'order_waiting',
      severity: 'warning',
      location_id: null,
      alert_group_id: null,
      send_email: true,
      send_in_app: true,
      cooldown_minutes: 15,
      threshold_minutes: 15,
      active_days: [0, 1, 2, 3, 4, 5, 6],
      is_active: true,
    });
    setEditingRule(null);
  };

  // Open rule modal for editing
  const openEditRule = (rule: AlertRule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      severity: rule.severity,
      location_id: rule.location_id,
      alert_group_id: rule.alert_group_id,
      send_email: rule.send_email,
      send_in_app: rule.send_in_app,
      cooldown_minutes: rule.cooldown_minutes,
      threshold_minutes: rule.conditions.threshold_minutes || 15,
      active_days: rule.active_days,
      is_active: rule.is_active,
    });
    setRuleModalOpen(true);
  };

  // Save rule
  const handleSaveRule = async () => {
    try {
      // Build conditions based on rule type
      const conditions: Record<string, any> = {
        threshold_minutes: ruleForm.threshold_minutes,
      };
      
      if (ruleForm.rule_type === 'order_waiting') {
        conditions.order_types = ['dine_in', 'takeout', 'delivery'];
        conditions.order_statuses = ['open', 'sent'];
      } else if (ruleForm.rule_type === 'kitchen_order_waiting') {
        conditions.kitchen_statuses = ['pending', 'in_progress'];
      }

      if (editingRule) {
        await updateRule.mutateAsync({
          id: editingRule.id,
          name: ruleForm.name,
          description: ruleForm.description || null,
          rule_type: ruleForm.rule_type,
          severity: ruleForm.severity,
          location_id: ruleForm.location_id,
          alert_group_id: ruleForm.alert_group_id,
          send_email: ruleForm.send_email,
          send_in_app: ruleForm.send_in_app,
          cooldown_minutes: ruleForm.cooldown_minutes,
          conditions,
          active_days: ruleForm.active_days,
          is_active: ruleForm.is_active,
        });
        toast.success('Alert rule updated successfully');
      } else {
        await createRule.mutateAsync({
          name: ruleForm.name,
          description: ruleForm.description || null,
          rule_type: ruleForm.rule_type,
          severity: ruleForm.severity,
          location_id: ruleForm.location_id,
          alert_group_id: ruleForm.alert_group_id,
          additional_user_ids: [],
          send_email: ruleForm.send_email,
          send_in_app: ruleForm.send_in_app,
          cooldown_minutes: ruleForm.cooldown_minutes,
          max_alerts_per_hour: 10,
          conditions,
          active_start_time: null,
          active_end_time: null,
          active_days: ruleForm.active_days,
          is_active: ruleForm.is_active,
        });
        toast.success('Alert rule created successfully');
      }
      setRuleModalOpen(false);
      resetRuleForm();
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to save alert rule',
      });
    }
  };

  // Delete rule
  const handleDeleteRule = async () => {
    if (!deleteRuleId) return;
    try {
      await deleteRule.mutateAsync(deleteRuleId);
      toast.success('Alert rule deleted');
      setDeleteRuleId(null);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to delete alert rule',
      });
    }
  };

  // Reset group form
  const resetGroupForm = () => {
    setGroupForm({ name: '', description: '' });
    setEditingGroup(null);
  };

  // Save group
  const handleSaveGroup = async () => {
    try {
      if (editingGroup) {
        await updateGroup.mutateAsync({
          id: editingGroup.id,
          name: groupForm.name,
          description: groupForm.description || undefined,
        });
        toast.success('Alert group updated successfully');
      } else {
        await createGroup.mutateAsync({
          name: groupForm.name,
          description: groupForm.description || undefined,
        });
        toast.success('Alert group created successfully');
      }
      setGroupModalOpen(false);
      resetGroupForm();
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to save alert group',
      });
    }
  };

  // Delete group
  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return;
    try {
      await deleteGroup.mutateAsync(deleteGroupId);
      toast.success('Alert group deleted');
      setDeleteGroupId(null);
      if (selectedGroupId === deleteGroupId) {
        setSelectedGroupId(null);
      }
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to delete alert group',
      });
    }
  };

  // Add member to group
  const handleAddMember = async (userId: string) => {
    if (!selectedGroupId) return;
    try {
      await addMember.mutateAsync({
        alert_group_id: selectedGroupId,
        user_id: userId,
      });
      toast.success('Member added to group');
      setAddMemberModalOpen(false);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to add member',
      });
    }
  };

  // Remove member from group
  const handleRemoveMember = async (memberId: string) => {
    if (!selectedGroupId) return;
    try {
      await removeMember.mutateAsync({ id: memberId, groupId: selectedGroupId });
      toast.success('Member removed from group');
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to remove member',
      });
    }
  };

  // Get members not in the selected group
  const availableMembers = teamMembers.filter(
    member => !groupMembers.some(gm => gm.user_id === member.id)
  );

  // Approval handlers
  const handleApprove = async () => {
    if (!selectedApproval || !verificationCode) return;

    setApprovalError(null);
    try {
      await approve({ approvalId: selectedApproval.id, code: verificationCode });
      setSelectedApproval(null);
      setVerificationCode('');
      toast.success('Approved', { description: 'The action has been approved successfully.' });
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : 'Failed to approve');
    }
  };

  const handleDeny = async (approval: PendingApproval) => {
    try {
      await deny(approval.id);
      toast.success('Denied', { description: 'The action has been denied.' });
    } catch (err) {
      console.error('Failed to deny:', err);
      toast.error('Error', { description: 'Failed to deny request' });
    }
  };

  // Filter approvals based on search
  const filteredApprovals = pendingApprovals.filter((approval) => {
    if (!approvalSearchTerm) return true;
    const description = getActionDescription(approval).toLowerCase();
    const actionType = formatActionType(approval.action_type).toLowerCase();
    const search = approvalSearchTerm.toLowerCase();
    return description.includes(search) || actionType.includes(search);
  });

  const filteredHistory = (approvalHistory || []).filter((item) => {
    if (!approvalSearchTerm) return true;
    const actionType = formatActionType(item.action_type).toLowerCase();
    const search = approvalSearchTerm.toLowerCase();
    return actionType.includes(search) || item.requester_name.toLowerCase().includes(search);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-semibold">Alerts & Notifications</h2>
          <p className="text-sm text-muted-foreground">
            Configure SLA alerts and notification routing
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('rules')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'rules'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Bell className="h-4 w-4 inline-block mr-2" />
          Alert Rules
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'groups'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Users className="h-4 w-4 inline-block mr-2" />
          Notification Groups
        </button>
        {canViewApprovals && (
          <button
            onClick={() => setActiveTab('approvals')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2',
              activeTab === 'approvals'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Shield className="h-4 w-4" />
            Approvals
            {pendingApprovals.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-xs">
                {pendingApprovals.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Info text for Approvals tab */}
      {activeTab === 'approvals' && canViewApprovals && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg px-4 py-3">
          <Info className="h-4 w-4 shrink-0" />
          <span>
            Review and approve sensitive actions that require owner 2FA verification, 
            such as voids, refunds, role changes, and security settings.
          </span>
        </div>
      )}

      {/* Alert Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetRuleForm(); setRuleModalOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Alert Rule
            </Button>
          </div>

          {rulesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Alert Rules</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create alert rules to get notified about important events
                </p>
                <Button onClick={() => { resetRuleForm(); setRuleModalOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Notify</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{rule.name}</p>
                          {rule.description && (
                            <p className="text-xs text-muted-foreground">{rule.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{getRuleTypeLabel(rule.rule_type)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSeverityColor(rule.severity)}>
                          {rule.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {rule.location?.name || 'All Locations'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {rule.send_email && (
                            <Badge variant="outline" className="text-xs">Email</Badge>
                          )}
                          {rule.send_in_app && (
                            <Badge variant="outline" className="text-xs">In-App</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditRule(rule)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteRuleId(rule.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* Notification Groups Tab */}
      {activeTab === 'groups' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Groups List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Groups</h3>
              <Button size="sm" onClick={() => { resetGroupForm(); setGroupModalOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                New Group
              </Button>
            </div>

            {groupsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : groups.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No notification groups created yet
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {groups.map((group) => (
                  <Card
                    key={group.id}
                    className={cn(
                      'cursor-pointer transition-colors hover:border-primary/50',
                      selectedGroupId === group.id && 'border-primary'
                    )}
                    onClick={() => setSelectedGroupId(group.id)}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{group.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {group.member_count || 0} members
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setEditingGroup(group);
                                setGroupForm({ name: group.name, description: group.description || '' });
                                setGroupModalOpen(true);
                              }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteGroupId(group.id);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Group Details Panel */}
          <div className="space-y-6">
            {!selectedGroupId ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Select a group to view and manage members and subscriptions
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Group Members Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">Group Members</h3>
                    <Button size="sm" onClick={() => setAddMemberModalOpen(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Member
                    </Button>
                  </div>

                  {groupMembers.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <UserPlus className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm text-muted-foreground mb-4">
                          No members in this group yet
                        </p>
                        <Button size="sm" onClick={() => setAddMemberModalOpen(true)}>
                          Add First Member
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Member</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>In-App</TableHead>
                            <TableHead className="w-[60px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupMembers.map((member) => (
                            <TableRow key={member.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                                    {member.user?.first_name?.[0]}{member.user?.last_name?.[0]}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {member.user?.first_name} {member.user?.last_name}
                                    </p>
                                    <p className="text-xs text-muted-foreground capitalize">
                                      {member.user?.role}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {member.notification_email ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell>
                                {member.notification_in_app ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-700"
                                  onClick={() => handleRemoveMember(member.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                  )}
                </div>

                {/* Alert Subscriptions Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">Alert Subscriptions</h3>
                      <p className="text-xs text-muted-foreground">
                        Select which alerts this group should receive
                      </p>
                    </div>
                  </div>

                  <Card>
                    {rules.length === 0 ? (
                      <CardContent className="py-8 text-center">
                        <Bell className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm text-muted-foreground">
                          No alert rules created yet. Create rules in the Alert Rules tab.
                        </p>
                      </CardContent>
                    ) : (
                      <div className="divide-y">
                        {rules.map((rule) => {
                          const isSubscribed = rule.alert_group_id === selectedGroupId;
                          return (
                            <div
                              key={rule.id}
                              className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  id={`rule-${rule.id}`}
                                  checked={isSubscribed}
                                  onCheckedChange={async (checked) => {
                                    try {
                                      await updateRule.mutateAsync({
                                        id: rule.id,
                                        alert_group_id: checked ? selectedGroupId : null,
                                      });
                                      toast.success(checked ? 'Subscribed to alert' : 'Unsubscribed from alert', {
                                        description: `${groups.find(g => g.id === selectedGroupId)?.name} ${checked ? 'will now receive' : 'will no longer receive'} "${rule.name}" alerts`,
                                      });
                                    } catch (error) {
                                      toast.error('Error', {
                                        description: 'Failed to update subscription',
                                      });
                                    }
                                  }}
                                  disabled={updateRule.isPending}
                                />
                                <div className="space-y-1">
                                  <label
                                    htmlFor={`rule-${rule.id}`}
                                    className="font-medium text-sm cursor-pointer"
                                  >
                                    {rule.name}
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      {getRuleTypeLabel(rule.rule_type)}
                                    </Badge>
                                    <Badge className={cn('text-xs', getSeverityColor(rule.severity))}>
                                      {rule.severity}
                                    </Badge>
                                    {rule.location?.name && (
                                      <span className="text-xs text-muted-foreground">
                                        {rule.location.name}
                                      </span>
                                    )}
                                  </div>
                                  {rule.description && (
                                    <p className="text-xs text-muted-foreground">{rule.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {!rule.is_active && (
                                  <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                )}
                                {isSubscribed && rule.alert_group_id === selectedGroupId && (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Subscribed</Badge>
                                )}
                                {rule.alert_group_id && rule.alert_group_id !== selectedGroupId && (
                                  <span className="text-xs text-muted-foreground">
                                    → {groups.find(g => g.id === rule.alert_group_id)?.name || 'Other Group'}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && canViewApprovals && (
        <div className="space-y-6">
          {/* Search */}
          <div className="flex justify-end">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search approvals..."
                value={approvalSearchTerm}
                onChange={(e) => setApprovalSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>

          {/* Pending Approvals */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Pending Approvals
              {pendingApprovals.length > 0 && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                  {pendingApprovals.length}
                </Badge>
              )}
            </h3>

            {approvalsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredApprovals.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-semibold mb-2">All caught up!</h3>
                  <p className="text-sm text-muted-foreground">
                    {approvalSearchTerm
                      ? 'No approvals match your search'
                      : 'No pending approvals at this time'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredApprovals.map((approval) => (
                  <Card key={approval.id}>
                    <CardContent className="p-6">
                      {selectedApproval?.id === approval.id ? (
                        // Verification Form
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold">Enter your 2FA code to approve</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {getActionDescription(approval)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedApproval(null);
                                setVerificationCode('');
                                setApprovalError(null);
                              }}
                            >
                              <X className="h-5 w-5" />
                            </Button>
                          </div>

                          {approvalError && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4" />
                              {approvalError}
                            </div>
                          )}

                          <div className="flex items-center gap-4">
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={6}
                              value={verificationCode}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setVerificationCode(value);
                              }}
                              placeholder="000000"
                              className="w-48 text-center text-2xl font-mono tracking-widest"
                              autoFocus
                            />
                            <Button
                              onClick={handleApprove}
                              disabled={verificationCode.length !== 6 || isApproving}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {isApproving ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <>
                                  <Check className="h-5 w-5 mr-2" />
                                  Approve
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Approval Card
                        <div>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <Badge className="bg-amber-100 text-amber-700 mb-2">
                                {formatActionType(approval.action_type)}
                              </Badge>
                              <h4 className="font-semibold">
                                {getActionDescription(approval)}
                              </h4>
                              <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                                <span>Requested by {approval.requester_name || 'Unknown'}</span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDistanceToNow(new Date(approval.created_at), { addSuffix: true })}
                                </span>
                              </div>
                            </div>

                            {/* Time remaining */}
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Expires</p>
                              <p className={cn(
                                'text-sm font-medium',
                                new Date(approval.expires_at) < new Date(Date.now() + 60000)
                                  ? 'text-red-600'
                                  : ''
                              )}>
                                {formatDistanceToNow(new Date(approval.expires_at))}
                              </p>
                            </div>
                          </div>

                          {/* Action metadata preview */}
                          {approval.action_metadata && Object.keys(approval.action_metadata).length > 0 && (
                            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                              <p className="text-xs font-medium text-muted-foreground mb-2">Details</p>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(approval.action_metadata)
                                  .filter(([key]) => !['description'].includes(key))
                                  .slice(0, 4)
                                  .map(([key, value]) => (
                                    <div key={key}>
                                      <span className="text-muted-foreground capitalize">
                                        {key.replace(/_/g, ' ')}:
                                      </span>{' '}
                                      <span>
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-3">
                            <Button
                              onClick={() => setSelectedApproval(approval)}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Approve with 2FA
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleDeny(approval)}
                              disabled={isDenying}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Deny
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Approval History */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              Approval History
            </h3>

            <Card>
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredHistory.length === 0 ? (
                <CardContent className="py-12 text-center">
                  <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">
                    {approvalSearchTerm ? 'No history matches your search' : 'No approval history yet'}
                  </p>
                </CardContent>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Approved By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-700">
                            {formatActionType(item.action_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.requester_name}</TableCell>
                        <TableCell>{item.approver_name || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.resolved_at && format(new Date(item.resolved_at), 'MMM d, yyyy h:mm a')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      <ResponsiveModal open={ruleModalOpen} onOpenChange={setRuleModalOpen}>
        <ResponsiveModalContent className="max-w-lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Configure when and how alerts should be triggered
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="e.g., Order Wait Time Alert"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={ruleForm.description}
                onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select
                  value={ruleForm.rule_type}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v as AlertRuleType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={ruleForm.severity}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, severity: v as AlertSeverity })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(ruleForm.rule_type === 'order_waiting' || ruleForm.rule_type === 'kitchen_order_waiting') && (
              <div className="space-y-2">
                <Label>Threshold (minutes)</Label>
                <Input
                  type="number"
                  value={ruleForm.threshold_minutes}
                  onChange={(e) => setRuleForm({ ...ruleForm, threshold_minutes: parseInt(e.target.value) || 15 })}
                  min={1}
                  max={120}
                />
                <p className="text-xs text-muted-foreground">
                  {ruleForm.rule_type === 'kitchen_order_waiting' 
                    ? 'Alert when kitchen prep time exceeds this duration'
                    : 'Alert when orders wait longer than this time'
                  }
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  value={ruleForm.location_id || 'all'}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, location_id: v === 'all' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notify Group</Label>
                <Select
                  value={ruleForm.alert_group_id || 'none'}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, alert_group_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a group...</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Active Days</Label>
              <div className="flex gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => {
                      const days = ruleForm.active_days.includes(day.value)
                        ? ruleForm.active_days.filter(d => d !== day.value)
                        : [...ruleForm.active_days, day.value];
                      setRuleForm({ ...ruleForm, active_days: days });
                    }}
                    className={cn(
                      'px-2 py-1 text-xs rounded border transition-colors',
                      ruleForm.active_days.includes(day.value)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted border-input'
                    )}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={ruleForm.send_email}
                  onCheckedChange={(v) => setRuleForm({ ...ruleForm, send_email: v })}
                />
                <Label>Email</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={ruleForm.send_in_app}
                  onCheckedChange={(v) => setRuleForm({ ...ruleForm, send_in_app: v })}
                />
                <Label>In-App</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={ruleForm.is_active}
                  onCheckedChange={(v) => setRuleForm({ ...ruleForm, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cooldown (minutes)</Label>
              <Input
                type="number"
                value={ruleForm.cooldown_minutes}
                onChange={(e) => setRuleForm({ ...ruleForm, cooldown_minutes: parseInt(e.target.value) || 15 })}
                min={1}
                max={120}
              />
              <p className="text-xs text-muted-foreground">
                Minimum time between repeated alerts for the same condition
              </p>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setRuleModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRule}
              disabled={!ruleForm.name || createRule.isPending || updateRule.isPending}
            >
              {(createRule.isPending || updateRule.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Group Modal */}
      <ResponsiveModal open={groupModalOpen} onOpenChange={setGroupModalOpen}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingGroup ? 'Edit Group' : 'Create Notification Group'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Groups allow you to route alerts to multiple people
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder="e.g., Kitchen Managers"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setGroupModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveGroup}
              disabled={!groupForm.name || createGroup.isPending || updateGroup.isPending}
            >
              {(createGroup.isPending || updateGroup.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Add Member Modal */}
      <ResponsiveModal open={addMemberModalOpen} onOpenChange={setAddMemberModalOpen}>
        <ResponsiveModalContent className="max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Add Member to Group</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Select a team member to add to this notification group
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody>
            {availableMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                All team members are already in this group
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableMembers.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => handleAddMember(member.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {member.first_name?.[0]}{member.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-medium">{member.first_name} {member.last_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ResponsiveModalBody>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Rule Confirmation */}
      <ResponsiveModal open={!!deleteRuleId} onOpenChange={() => setDeleteRuleId(null)}>
        <ResponsiveModalContent className="max-w-sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Delete Alert Rule?</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              This action cannot be undone. The rule will stop triggering alerts.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setDeleteRuleId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRule}
              disabled={deleteRule.isPending}
            >
              {deleteRule.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Rule
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Group Confirmation */}
      <ResponsiveModal open={!!deleteGroupId} onOpenChange={() => setDeleteGroupId(null)}>
        <ResponsiveModalContent className="max-w-sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Delete Notification Group?</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              This will remove all members from the group. Alert rules using this group will need to be updated.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setDeleteGroupId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteGroup}
              disabled={deleteGroup.isPending}
            >
              {deleteGroup.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Group
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}

